<script setup>
import { onBeforeUnmount } from 'vue'
import { useChartStore } from './stores'
import { storeToRefs } from 'pinia'
import { configuration } from '@dpgradio/creative'
import { debounce } from './utils/debounce'

import Track from './components/Track.ce.vue'
import Pagination from './components/Pagination.ce.vue'
import HiddenTracks from './components/HiddenTracks.ce.vue'

const props = defineProps({
  listId: {
    type: String,
    required: true,
  },
  editionId: {
    type: String,
    required: false,
  },
  stationId: {
    type: String,
    required: false
  },
  preview: {
    type: Number,
    required: false
  },
  expandedChartLink: {
    type: String,
    required: false
  }
})

const chartStore = useChartStore()
const { chart, statsForTrack, isLoading, isPreview, isSearching, currentStart, hiddenTracksCount, currentTrack } = storeToRefs(chartStore)
const { setupChart } = chartStore

setupChart({ listId: props.listId, editionId: props.editionId, stationId: props.stationId, preview: props.preview })

const brand = configuration.stationId.split("_")[0] // TODO: get from configuration

const handleRangeChange = async ({ from, to }) => {
  await chartStore.fetchChart({ from, to })
}

const showFullList = () => {
  chartStore.isPreview = false
  chartStore.tracksToPreview = null
  chartStore.fetchChart({ limit: 100 })
}

const debouncedStoreSearch = debounce(chartStore.search, 300)

const search = (e) => {
  const value = e?.target?.value ?? ''
  debouncedStoreSearch(value)
}

onBeforeUnmount(() => {
  debouncedStoreSearch.cancel()
})

</script>

<template>
  <div v-if="chart" :class="`${brand}-theme`" class="chart-embed">
    <div class="current-track" v-if="currentTrack && currentTrack.position != chart.tracks[0].position">
      <h2>Huidig nummer</h2>

      <Track v-bind="currentTrack" />
    </div>

    <div class="chart-header">
      <h1>{{ chart.edition_name }}</h1>

      <div class="chart-actions" v-if="!isPreview">
        <input type="text" name="search" placeholder="Zoek op titel of artiest" @input="search" />
      </div>
    </div>

    <div v-if="isLoading">Bezig met laden…</div>
    <template v-else>
      <HiddenTracks v-if="hiddenTracksCount" :count="hiddenTracksCount" />
      <Track
        v-for="track in chart.tracks"
        :key="track.position"
        v-bind="track"
        :stats="statsForTrack(track)"
      />
    </template>
    <div v-if="isPreview">
      <a v-if="expandedChartLink" class="preview-link" :href="expandedChartLink">Bekijk de volledige lijst</a>
      <a v-else class="preview-link" href="#" @click.prevent="showFullList">Bekijk de volledige lijst</a>
    </div>
    <Pagination
      v-if="chart.total && !isPreview && !isSearching"
      :total="chart.total"
      :step="100"
      :first-position="chartStore.firstPosition"
      :current-start="currentStart"
      @update:range="handleRangeChange"
    />
  </div>
</template>

<style lang="scss">
@import './main.css';

.chart-embed {
  font-family: var(--font-body);
  margin-bottom: 1rem;

  h1, h2 {
    font-family: var(--font-title);
    font-weight: 600;
    text-transform: uppercase;
  }

  .chart-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1rem;

    h1 {
      margin: 0;
    }

    input {
      border: 1px solid rgb(var(--color-element-dark));
      padding: 0.5rem;
      font-size: 1rem;
    }
  }

  .preview-link {
    display: block;
    font-family: var(--font-important);
    text-align: center;
    width: fit-content;
    padding: 0.5rem 1rem;
    margin: 1rem auto;
    font-size: 1.2rem;
    font-weight: 600;
    text-transform: uppercase;
    background-color: rgb(var(--color-primary));
    color: rgb(var(--color-accent-c));
    text-decoration: none;
  }
}
</style>